import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useChangePositionStatus, type PositionRow } from '../api/organisation-api';
import styles from './NewUnitDialog.module.css';

/**
 * F3.04 — freeze a position.
 *
 * Its own dialog for one reason: L7 makes a budget freeze a manual SLA pause, and
 * a manual pause requires a written reason. Non-negotiable #2 in its general form —
 * the action stays disabled until one is given, rather than being accepted and
 * chased up afterwards.
 *
 * The dialog also says out loud what freezing does to the clock, because "frozen"
 * on its own does not tell an HR Lead that a recruiter's SLA stops running.
 */
export function FreezeDialog({
  position,
  onClose,
}: {
  readonly position: PositionRow;
  readonly onClose: () => void;
}): ReactNode {
  const showToast = useToasts((state) => state.show);
  const changeStatus = useChangePositionStatus();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    try {
      await changeStatus.mutateAsync({
        id: position.id,
        version: position.version,
        status: 'frozen',
        reason: reason.trim(),
      });
      showToast({ tone: 'success', message: `${position.code} frozen` });
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Freeze ${position.code}`}
      description="A frozen seat stays out of active recruitment counts, and any SLA clock against it stops running until it is unfrozen."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            isPending={changeStatus.isPending}
            // Refused until a reason exists — the rule, not a hint about it.
            disabled={reason.trim() === ''}
            onClick={() => void submit()}
          >
            Freeze it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field
          label="Why"
          isRequired
          hint="This shows on the seat and in its history, so whoever finds it in three months knows what happened."
        >
          {({ inputId, isInvalid }) => (
            <Textarea
              id={inputId}
              isInvalid={isInvalid}
              value={reason}
              rows={3}
              autoFocus
              onChange={(event) => setReason(event.target.value)}
              placeholder="Budget frozen until the Q3 review."
            />
          )}
        </Field>

        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
